import actionCreators from 'actionCreators';
import UserPanelContainer from 'components/UserPanel';
import {updateCoverPageImages} from 'ducks/coverPageImages';
import {updateDisclaimers} from 'ducks/disclaimers';
import {resetPropertyDetail} from 'ducks/propertyDetail';
import {toggleFloorPlan} from 'ducks/reportSections';
import {loadSettings} from 'ducks/settings';
import {loadUserDetails} from 'ducks/userDetails';
import 'generated/styleguide/semantic.css';
import AnalyticsGateway, {parsePath} from 'lib/AnalyticsGateway';
import {isNZLocale} from 'lib/PropertyUtils';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {Component} from 'react';
import {push} from 'react-router-redux';
/* istanbul ignore next */
import {Dropdown, Menu} from 'semantic-ui-react';
import connected from '../connected';
import './TopBar.scss';
import {TrainingLinkContainer} from 'components/TrainingLink';
import FeedbackLink from './FeedbackLink/FeedbackLink';
import S3Util from 'lib/S3Util';
import {fetchPropertyAvm} from 'actionCreators/propertyFetchingActions';

export class TopBar extends Component {
  static propTypes = {
    userDetails: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object,
    logos: PropTypes.object,
    reportType: PropTypes.object.isRequired,
    propertyDetail: PropTypes.object.isRequired,
    featureFlags: PropTypes.shape({
      rapidSearchEnabled: PropTypes.bool,
      nzRentalCma: PropTypes.bool,
      propertyPriceStatement: PropTypes.bool,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
    login: PropTypes.shape({loggedIn: PropTypes.bool.isRequired}).isRequired,
  };

  componentDidMount() {
    const {dispatch, login: {loggedIn}, userDetails, location: {pathname, search}} = this.props;

    let reportTypeQueryParam = queryString.parse(search).reportType;
    if (reportTypeQueryParam && ['cma', 'soi', 'rental'].indexOf(reportTypeQueryParam) >= 0) {
      dispatch(actionCreators.chooseReportType(reportTypeQueryParam));
    } else if (pathname) {
      const reportTypeMatch = /\/properties\/(\d+)\/(rental|cma|soi)\/.*/.exec(pathname);
      if (reportTypeMatch) {
        dispatch(resetPropertyDetail({propertyId: parseInt(reportTypeMatch[1], 10)}));
        dispatch(actionCreators.chooseReportType(reportTypeMatch[2]));
      }
    }

    if (loggedIn) {
      dispatch(updateCoverPageImages());
      dispatch(updateDisclaimers());
      if (!userDetails.firstName) {
        dispatch(loadUserDetails());
      }
    }
    dispatch(loadSettings());

    this.updateTitle();
  }

  componentDidUpdate(prevProps) {
    const {dispatch, reportType: {value: reportType}, location: {pathname, search}} = this.props;

    let reportTypeQueryParam = queryString.parse(search).reportType;
    if (reportType !== reportTypeQueryParam && reportTypeQueryParam && ['cma', 'soi', 'rental'].indexOf(reportTypeQueryParam) >= 0) {
      dispatch(actionCreators.chooseReportType(reportTypeQueryParam));
    } else if (pathname) {
      const reportTypeMatch = /\/properties\/(\d+)\/(rental|cma|soi)\/.*/.exec(pathname);
      if (reportTypeMatch && reportType !== reportTypeMatch[2]) {
        dispatch(actionCreators.chooseReportType(reportTypeMatch[2]));
      }
    }
    this.updateTitle();
  }

  navigateHome = () => {
    this.sendAnalyticsEvent();
    window.open('/', '_self');
  };

  sendAnalyticsEvent = () => {
    const {history: {location: {pathname}}} = this.props;
    AnalyticsGateway.sendEvent({
      eventName: 'Click Signature Logo',
      properties: [{label: 'Path', value: parsePath(pathname)}]
    });
  };

  setReportType = (reportType) => () => {
    const {dispatch, location: {pathname}, propertyDetail, reportType: {value: existingValue}} = this.props;
    const reportTypeMatch = /\/properties\/(\d+)\/(rental|cma|soi)\/.*/.exec(pathname);
    if (reportTypeMatch && reportTypeMatch[2] === reportType) {
      return;
    }
    dispatch(actionCreators.chooseReportType(reportType));
    dispatch(toggleFloorPlan(reportType, propertyDetail.floorPlanUris));
    const propertyId = reportTypeMatch ? reportTypeMatch[1] : pathname.match(/^\/properties\/(\d+)/)[1];
    dispatch(push(`/properties/${propertyId}/${reportType}/${reportType === 'rental' ? 'comparable_rentals' : 'comparable_sales'}`));

    // Re-fetch AVM when switching to or from rental report type,
    // since rental and sales AVMs are different data sets.
    // This is only reachable from the property page (TopBar hides the dropdown on the home page).
    const isReportTypeChanged = reportType !== existingValue;
    const isRentalInvolved = existingValue === 'rental' || reportType === 'rental';
    if (propertyId && isReportTypeChanged && isRentalInvolved) {
      dispatch(fetchPropertyAvm({propertyId}));
    }
  };

  updateTitle = () => {
    const {
      propertyDetail: {singleLineAddress},
      reportType: {value: reportType},
      featureFlags: {propertyPriceStatement = false} = {},
    } = this.props;
    const typePrefixes = {
      cma: 'Sales CMA',
      rental: 'Rental CMA',
      soi: propertyPriceStatement ? 'PPS' : 'SOI',
    };

    let titlePrefix = reportType && singleLineAddress ? `${typePrefixes[reportType]}: ${singleLineAddress} - ` : '';
    let titleSuffix = isNZLocale() ? 'Signature Reports' : 'RP Data Reports';
    document.title = `${titlePrefix}${titleSuffix}`;
  };

  render() {
    const {
      userDetails,
      propertyDetail: { addressState },
      reportType,
      location: {pathname},
      dispatch,
      featureFlags: {
        rapidSearchEnabled = false,
        nzRentalCma = false,
        propertyPriceStatement = false,
      },
    } = this.props;

    const reportTypeDescription = {
      soi: propertyPriceStatement ? 'Property Price Statement' : 'Statement of Information',
      cma: 'Comparative Market Analysis',
      rental: 'Rental CMA',
    }[reportType.value];

    const nzRentalCmaEnabled = rapidSearchEnabled && nzRentalCma;

    return (
      <Menu className="TopBar" inverted attached borderless>
        <Menu.Item className="signature-home-link" onClick={this.navigateHome}>
          <img
              className={`logo${isNZLocale() ? '-nz' : ''}`}
              src={S3Util.getAssetDomain(`/logos/${isNZLocale() ? 'pg' : 'rp'}-header.svg`)}
              alt="logo"
          />
        </Menu.Item>
        <Menu.Item position="right">
          {userDetails.admin && (
            <Dropdown item text="Admin" className="admin">
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => dispatch(push('/admin/photos'))}>Photos</Dropdown.Item>
                <Dropdown.Item onClick={() => dispatch(push('/admin/notes'))}>Notes</Dropdown.Item>
                <Dropdown.Item onClick={() => dispatch(push('/admin/assets'))}>Assets</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
          {/^\/properties\/\d+/.test(pathname) && (
            <Dropdown item className="reportType" trigger={<span>Report Type: <span className="selected-report-type">{reportTypeDescription}</span></span>}>
              <Dropdown.Menu>
                <Dropdown.Item className="cma-item" onClick={this.setReportType('cma')}
                               active={reportType.value === 'cma'}>
                  Comparative Market Analysis
                </Dropdown.Item>
                {(!isNZLocale() || nzRentalCmaEnabled) && (
                  <Dropdown.Item className="rental-item" onClick={this.setReportType('rental')}
                                 active={reportType.value === 'rental'}>
                    Rental CMA
                  </Dropdown.Item>
                )}
                {addressState === 'VIC' && (
                  <Dropdown.Item className="soi-item" onClick={this.setReportType('soi')}
                                 active={reportType.value === 'soi'}>
                    {propertyPriceStatement ? 'Property Price Statement' : 'Statement of Information'} [Note: VIC only]
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <UserPanelContainer>
            <TrainingLinkContainer className="action training"/>
            <hr/>
            <FeedbackLink className="action feedback-button">Your Feedback</FeedbackLink>
          </UserPanelContainer>
        </Menu.Item>
      </Menu>
    );
  }
}

export default connected(TopBar);
